import React from 'react';
import { Skeleton } from './skeleton';

/**
 * Page-level skeleton screens.
 * Use these instead of blocking spinners to give the app an "instant" feel
 * while data loads. Each skeleton mirrors the rough layout of its real page.
 */

export const FeedSkeleton = () => (
  <div data-testid="feed-skeleton" className="min-h-screen bg-slate-50 pb-20 md:pb-6">
    <div className="sticky top-0 z-40 bg-white border-b border-slate-200 px-4 py-3">
      <div className="max-w-2xl mx-auto flex items-center justify-between">
        <Skeleton className="h-8 w-24" />
        <div className="flex gap-2">
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-8 w-20" />
        </div>
      </div>
    </div>
    <div className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
      {/* Stories row */}
      <div className="flex gap-3 overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="flex flex-col items-center gap-1">
            <Skeleton className="h-16 w-16 rounded-full" />
            <Skeleton className="h-3 w-12" />
          </div>
        ))}
      </div>
      {/* Post cards */}
      {[...Array(3)].map((_, i) => (
        <div key={i} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="p-3 flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
          <Skeleton className="h-72 w-full rounded-none" />
          <div className="p-3 space-y-2">
            <div className="flex gap-3">
              <Skeleton className="h-6 w-6 rounded-full" />
              <Skeleton className="h-6 w-6 rounded-full" />
              <Skeleton className="h-6 w-6 rounded-full" />
            </div>
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const TripsSkeleton = () => (
  <div data-testid="trips-skeleton" className="min-h-screen bg-slate-50 pb-20 md:pb-6">
    <div className="max-w-4xl mx-auto px-4 pt-6 space-y-4">
      <Skeleton className="h-9 w-40" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <Skeleton className="h-40 w-full rounded-none" />
            <div className="p-4 space-y-2">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
              <div className="flex gap-2 pt-2">
                <Skeleton className="h-6 w-16 rounded-full" />
                <Skeleton className="h-6 w-20 rounded-full" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const ChatListSkeleton = () => (
  <div data-testid="chat-list-skeleton" className="min-h-screen bg-white">
    <div className="px-4 py-3 border-b border-slate-200">
      <Skeleton className="h-7 w-32" />
    </div>
    <div className="divide-y divide-slate-100">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-2/3" />
          </div>
          <Skeleton className="h-3 w-10" />
        </div>
      ))}
    </div>
  </div>
);

export const ChatRoomSkeleton = () => (
  <div data-testid="chat-room-skeleton" className="min-h-screen bg-slate-50 flex flex-col">
    <div className="px-4 py-3 border-b border-slate-200 bg-white flex items-center gap-3">
      <Skeleton className="h-10 w-10 rounded-full" />
      <Skeleton className="h-5 w-32" />
    </div>
    <div className="flex-1 px-4 py-4 space-y-3">
      {[...Array(6)].map((_, i) => (
        <div key={i} className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
          <Skeleton className={`h-10 ${i % 3 === 0 ? 'w-56' : 'w-40'} rounded-2xl`} />
        </div>
      ))}
    </div>
  </div>
);

export const ProfileSkeleton = () => (
  <div data-testid="profile-skeleton" className="min-h-screen bg-white pb-20 md:pb-6">
    <div className="max-w-2xl mx-auto px-4 pt-6 space-y-4">
      <div className="flex items-center gap-4">
        <Skeleton className="h-20 w-20 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-24" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 pt-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="text-center space-y-1">
            <Skeleton className="h-6 w-12 mx-auto" />
            <Skeleton className="h-3 w-16 mx-auto" />
          </div>
        ))}
      </div>
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-5/6" />
      <Skeleton className="h-3 w-2/3" />
      <div className="grid grid-cols-3 gap-1 pt-4">
        {[...Array(9)].map((_, i) => (
          <Skeleton key={i} className="aspect-square w-full" />
        ))}
      </div>
    </div>
  </div>
);

export const NotificationsSkeleton = () => (
  <div data-testid="notifications-skeleton" className="min-h-screen bg-white">
    <div className="px-4 py-3 border-b border-slate-200">
      <Skeleton className="h-7 w-40" />
    </div>
    <div className="divide-y divide-slate-100">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const GenericPageSkeleton = () => (
  <div data-testid="generic-page-skeleton" className="min-h-screen bg-slate-50">
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-4">
      <Skeleton className="h-9 w-1/3" />
      <Skeleton className="h-4 w-2/3" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg border border-slate-200 p-4 space-y-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-5/6" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default {
  FeedSkeleton,
  TripsSkeleton,
  ChatListSkeleton,
  ChatRoomSkeleton,
  ProfileSkeleton,
  NotificationsSkeleton,
  GenericPageSkeleton,
};
